import { get_token } from "@/router";
import Swal from "sweetalert2";
import {
    get_delivery_contact_details,
    get_first_value,
    get_google_place_address_fields,
    load_google_places_script,
} from "./delivery_helpers";

const REQUIRED_DELIVERY_FIELDS = [
    "contact_name",
    "contact_phone",
    "street_address",
    "suburb",
    "city",
    "province",
    "postal_code",
];

function get_auth_headers() {
    return {
        Authorization: `Token ${get_token()}`,
        "Content-Type": "application/json",
    };
}

function show_delivery_toast(options) {
    Swal.fire({
        position: "bottom-end",
        showConfirmButton: false,
        toast: true,
        ...options,
    });
}

function get_address_user_details(context, include_user_fallback) {
    return include_user_fallback ? context.active_user_details : {};
}

function get_save_address_payload(address = {}) {
    return {
        contact_name: address.contact_name,
        email: address.email,
        contact_phone: address.contact_phone,
        street_address: address.street_address,
        suburb: address.suburb,
        city: address.city,
        province: address.province,
        postal_code: address.postal_code,
    };
}

export const delivery_address_methods = {
    get_delivery_address_payload() {
        return this.delivery_address_form;
    },
    emit_delivery_address() {
        this.clear_local_validation_errors(this.delivery_address_form);
        this.$emit("delivery-address-updated", this.get_delivery_address_payload({ trim: false }));
    },
    get_field_error(field) {
        return Reflect.get(this.active_validation_errors || {}, field) || "";
    },
    get_field_input_class(field) {
        return Reflect.get(this.active_validation_errors || {}, field) ? "is-invalid" : "";
    },
    clear_local_validation_errors(address) {
        const current_address = address && typeof address === "object" ? address : {};
        const validation_errors = this.local_validation_errors || {};

        this.local_validation_errors = Object.fromEntries(
            Object.entries(validation_errors).filter(
                ([field]) => !Reflect.get(current_address, field)
            )
        );
    },
    normalize_user_addresses_response(response) {
        return Array.isArray(response?.data) ? response.data : [];
    },
    get_user_address_id(address) {
        return address?.id || address?.address_id || address?.user_address_id || null;
    },
    get_user_address_label(address) {
        return [
            address?.street_address,
            address?.suburb,
            address?.city,
            address?.province,
            address?.postal_code,
        ].filter(Boolean).join(", ");
    },
    normalize_user_address(address = {}, include_user_fallback = true) {
        const user_details = get_address_user_details(this, include_user_fallback);
        const contact_details = get_delivery_contact_details(user_details, address);
        return ({
            address_id: get_first_value(address.address_id, address.id),
            contact_name: String(contact_details.contact_name || ""),
            email: String(contact_details.email || ""),
            contact_phone: String(contact_details.contact_phone || ""),
            street_address: String(address.street_address),
            suburb: String(address.suburb),
            city: String(address.city),
            province: String(address.province),
            postal_code: String(address.postal_code),
        });
    },
    fetch_user_addresses() {
        this.saved_addresses_loading = true;
        $.ajax({
            url: `${process.env.VUE_APP_BACKEND_API_URL}/company_management/get_user_addresses/`,
            method: "POST",
            headers: get_auth_headers(),
            data: JSON.stringify({ user_id: this.active_user_details?.user_id }),
            success: (response) => {
                this.saved_user_addresses = this.normalize_user_addresses_response(response);
            },
            error: (xhr) => {
                console.error("Failed to fetch user addresses:", xhr.responseText);
                this.saved_user_addresses = [];
            },
            complete: () => {
                this.saved_addresses_loading = false;
            },
        });
    },
    select_user_address(address) {
        if (!address) return;
        this.selected_saved_address_id = address.id;
        this.delivery_address_form = {
            ...this.delivery_address_form,
            ...this.normalize_user_address(address, true),
        };
        this.emit_delivery_address();
    },
    get_missing_delivery_address_fields(address) {
        return REQUIRED_DELIVERY_FIELDS
            .map((field) => [field, this.get_delivery_field_label(field), Reflect.get(address, field)])
            .filter(([, , value]) => !value);
    },
    get_delivery_field_label(field) {
        return field === "suburb" ? "suburb / local area" : field.replace(/_/g, " ");
    },
    get_missing_delivery_address_message(missing_fields) {
        return `Please add the following delivery details:\n${missing_fields.map(([, label]) => `- ${label}`).join("\n")}`;
    },

    get_delivery_address_errors(missing_fields) {
        return Object.fromEntries(
            missing_fields.map(([field, label]) => [field, `Please add ${label}.`])
        );
    },

    show_missing_delivery_address_warning(missing_fields) {
        show_delivery_toast({
            icon: "warning",
            title: "Missing Delivery Details",
            text: this.get_missing_delivery_address_message(missing_fields),
            timer: 5000,
        });
    },

    handle_save_address_success(response) {
        const saved_address = response?.data;
        this.selected_saved_address_id = saved_address?.id || null;
        show_delivery_toast({
            icon: "success",
            title: "Saved",
            text: "Delivery address saved.",
            timer: 2000,
        });
        this.fetch_user_addresses();
    },

    show_address_request_error(xhr, title) {
        show_delivery_toast({
            icon: "error",
            title,
            text: xhr.responseJSON?.message || xhr.responseJSON?.error || "Please try again.",
            timer: 3000,
        });
    },

    save_user_address() {
        const address = this.get_delivery_address_payload();
        const missing_fields = this.get_missing_delivery_address_fields(address);
        this.local_validation_errors = this.get_delivery_address_errors(missing_fields);

        if (missing_fields.length) {
            this.show_missing_delivery_address_warning(missing_fields);
            return;
        }

        this.local_validation_errors = {};
        this.saved_address_saving = true;
        $.ajax({
            url: `${process.env.VUE_APP_BACKEND_API_URL}/company_management/save_user_address/`,
            method: "POST",
            headers: get_auth_headers(),
            data: JSON.stringify({
                user_id: this.active_user_details?.user_id,
                ...get_save_address_payload(address),
            }),
            success: this.handle_save_address_success,
            error: (xhr) => this.show_address_request_error(xhr, "Could not save address"),
            complete: () => {
                this.saved_address_saving = false;
            },
        });
    },

    remove_deleted_address(address_id) {
        this.saved_user_addresses = this.saved_user_addresses.filter((item) => {
            return this.get_user_address_id(item) !== address_id;
        });
        if (this.selected_saved_address_id === address_id) {
            this.selected_saved_address_id = null;
        }
    },

    handle_delete_address_success(address_id) {
        this.remove_deleted_address(address_id);
        show_delivery_toast({
            icon: "success",
            title: "Deleted",
            text: "Delivery address removed.",
            timer: 2000,
        });
    },

    delete_user_address(address) {
        const address_id = address.id;
        if (!address_id) return;

        this.deleting_address_id = address_id;
        $.ajax({
            url: `${process.env.VUE_APP_BACKEND_API_URL}/company_management/delete_user_address/`,
            method: "POST",
            headers: get_auth_headers(),
            data: JSON.stringify({
                user_id: this.active_user_details?.user_id,
                address_id,
                id: address_id,
            }),
            success: () => this.handle_delete_address_success(address_id),
            error: (xhr) => this.show_address_request_error(xhr, "Could not delete address"),
            complete: () => {
                this.deleting_address_id = null;
            },
        });
    },

    handle_delivery_address_input(event) {
        this.emit_delivery_address();
        this.init_delivery_address_autocomplete(event);
    },

    init_delivery_address_autocomplete(event) {
        const input = event?.target;
        if (!input || input.dataset.googleAutocompleteAttached === "true") return;

        load_google_places_script()
            .then(() => this.attach_delivery_address_autocomplete(input))
            .catch((error) => {
                console.error("Failed to load Google address suggestions:", error);
            });
    },

    attach_delivery_address_autocomplete(input) {
        if (!window.google?.maps?.places) return;

        const autocomplete = new window.google.maps.places.Autocomplete(input, {
            componentRestrictions: { country: "za" },
            fields: ["address_components", "formatted_address", "geometry", "name"],
            types: ["address"],
        });

        input.dataset.googleAutocompleteAttached = "true";
        autocomplete.addListener("place_changed", () => {
            this.apply_google_place_to_delivery_address(autocomplete.getPlace());
        });
    },

    apply_google_place_to_delivery_address(place) {
        if (!place) return;
        const address_fields = get_google_place_address_fields(place);
        this.delivery_address_form = {
            ...this.delivery_address_form,
            street_address: address_fields.street_address,
            suburb: address_fields.suburb,
            city: address_fields.city,
            province: address_fields.province,
            postal_code: address_fields.postal_code,
        };
        this.emit_delivery_address();
    },
};
